// Draws a subscribed domain event as one sentence beside a glyph for its
// family: a composed line whose names are entity pills, or the server's
// reviewed summary when no line can be built. Names outside the composer's
// allowlist draw nothing, and so does the one transition it keeps silent.
// Ids reach the markup only as a pill's link or click handler; the event
// name and raw data never do.
import { type ReactNode } from 'react';
import { type z } from 'zod';

import {
    composeDomainEventLine,
    domainEventGlyph,
    domainEventObservedPayloadSchema,
    isSilentDomainEvent,
} from '@apps/web/features/phoebe_chat_v3/registry/definitions/domain_event_lines';
import { defineStandalone } from '@apps/web/features/phoebe_chat_v3/registry/types';
import { EventLine } from '@apps/web/features/phoebe_chat_v3/ui/primitives/event_line';
import { useFormatPersonName } from '@apps/web/hooks/use_person_name_format';

const MAX_SUMMARY_LENGTH = 200;

export const domainEventObservedDefinitions = {
    domain_event_observed: defineStandalone('domain_event_observed', {
        disposition: 'render',
        schema: domainEventObservedPayloadSchema,
        malformed: { kind: 'skip' },
        evidence: null,
        group: null,
        render(payload) {
            return <DomainEventObservedBody payload={payload} />;
        },
    }),
} as const;

function DomainEventObservedBody({
    payload,
}: {
    readonly payload: z.infer<typeof domainEventObservedPayloadSchema>;
}): ReactNode {
    const { formatName } = useFormatPersonName();
    if (isSilentDomainEvent(payload)) {
        return null;
    }
    const line = composeDomainEventLine(payload, formatName);
    if (line !== null) {
        return <EventLine glyph={line.glyph}>{line.body}</EventLine>;
    }
    const glyph = domainEventGlyph(payload.event_name);
    // The server does not bound its summary; the line does, so an
    // unusually long one is cut rather than dropped.
    const summary = (payload.summary?.trim() ?? '').slice(
        0,
        MAX_SUMMARY_LENGTH,
    );
    if (glyph === null || summary === '') {
        return null;
    }
    return <EventLine glyph={glyph}>{summary}</EventLine>;
}

export const domainEventObservedContractFixtures = {
    valid: {
        type: 'domain_event_observed',
        app_event_id: '00000000-0000-4000-8000-000000000001',
        event_name: 'shift.status_changed',
        entity_type: 'shift',
        entity_id: '00000000-0000-4000-8000-000000000002',
        related_entities: [],
        occurred_at: '2026-09-02T12:00:00Z',
        summary: 'Shift status changed from Open to Cancelled',
        data: { previous_status: 'open', new_status: 'cancelled' },
        subscription_id: '00000000-0000-4000-8000-000000000004',
        delivery_mode: 'passive',
    },
    malformed: {
        type: 'domain_event_observed',
        app_event_id: 'not-a-uuid',
        event_name: 'shift.status_changed',
        entity_type: 'shift',
        entity_id: '00000000-0000-4000-8000-000000000002',
        related_entities: [],
        occurred_at: '2026-09-02T12:00:00Z',
        summary: null,
        data: {},
        subscription_id: '00000000-0000-4000-8000-000000000004',
        delivery_mode: 'passive',
    },
} as const;
